import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { StockActionDialog, type StockActionMode, type StockTarget } from '@/components/StockActionDialog';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';

/**
 * One SKU + one owner. Stock is never edited directly: a person with INVENTORY.WRITE
 * corrects it through a counted, reasoned, audited action (count / move / quarantine / write off).
 */
export function InventoryDetailPage(): JSX.Element {
  const { skuId, ownerId } = useParams<{ skuId: string; ownerId: string }>();
  const result = useApi(() => endpoints.inventoryDetail(skuId!, ownerId!), [skuId, ownerId]);
  const { t, locale } = useLanguage();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('INVENTORY.WRITE');
  const [action, setAction] = useState<{ mode: StockActionMode; target: StockTarget } | null>(null);
  const skuCodeForOwner = result.status === 'success' ? String((result.data.sku as { code?: string }).code ?? '') : '';
  // Best effort: the detail read carries only the owner id, the list read carries the name.
  const ownerLookup = useApi(
    () => (skuCodeForOwner ? endpoints.inventory({ ownerId: ownerId!, sku: skuCodeForOwner, limit: 1 }) : Promise.resolve(null)),
    [skuCodeForOwner, ownerId],
  );
  const ownerName =
    ownerLookup.status === 'success' && ownerLookup.data ? (ownerLookup.data.rows[0]?.owner_name ?? null) : null;

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;

  const { sku, balances, reservations, movements } = result.data;
  const skuRow = sku as { code: string; name: string; is_lot_tracked: boolean; is_serial_tracked: boolean };
  const tracked = skuRow.is_lot_tracked || skuRow.is_serial_tracked;

  function open(mode: StockActionMode, r: Record<string, unknown>): void {
    setAction({
      mode,
      target: {
        warehouseId: String(r.warehouse_id),
        locationId: String(r.location_id),
        locationCode: String(r.location_code ?? r.path),
        ownerId: ownerId!,
        ownerName,
        skuId: skuId!,
        skuLabel: `${skuRow.code} — ${skuRow.name}`,
        lotId: r.lot_id ? String(r.lot_id) : null,
        systemQty: String(r.qty_on_hand),
      },
    });
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          {skuRow.code} — {skuRow.name}
        </h1>
        {skuRow.is_serial_tracked && <span className="badge neutral">{t('inventoryDetail.serialTracked')}</span>}
        {skuRow.is_lot_tracked && <span className="badge neutral">{t('inventoryDetail.lotTracked')}</span>}
        {ownerName && <span className="sub">{t('found.seller', { name: ownerName })}</span>}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('inventoryDetail.balancesByLocation')}</h2>
        {balances.length === 0 ? (
          <p>{t('inventoryDetail.noBalances')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('inventoryDetail.colWarehouse')}</th>
                <th>{t('inventoryDetail.colLocation')}</th>
                <th>{t('inventory.colOnHand')}</th>
                <th>{t('inventory.colReserved')}</th>
                {canWrite && <th>{t('stx.actionsCol')}</th>}
              </tr>
            </thead>
            <tbody>
              {balances.map((row, i) => {
                const r = row as Record<string, unknown>;
                return (
                  <tr key={i}>
                    <td>{String(r.warehouse_name)}</td>
                    <td>{String(r.path)}</td>
                    <td>{formatNumber(String(r.qty_on_hand), locale)}</td>
                    <td>{formatNumber(String(r.qty_reserved), locale)}</td>
                    {canWrite && (
                      <td>
                        <div className="action-row">
                          <button type="button" onClick={() => open('COUNT', r)} disabled={tracked}>{t('stx.countAction')}</button>
                          <button type="button" onClick={() => open('MOVE', r)} disabled={tracked}>{t('stx.moveAction')}</button>
                          <button type="button" onClick={() => open('QUARANTINE', r)} disabled={tracked}>{t('stx.quarantineAction')}</button>
                          <button type="button" className="danger" onClick={() => open('WRITE_OFF', r)} disabled={tracked}>{t('stx.writeOffAction')}</button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
        {canWrite && tracked && <p className="field-hint">{t('stx.trackedHint')}</p>}
      </div>

      {action && (
        <StockActionDialog
          key={`${action.mode}:${action.target.locationId}:${action.target.lotId ?? ''}`}
          mode={action.mode}
          target={action.target}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            result.reload();
          }}
        />
      )}

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('inventoryDetail.openReservations')}</h2>
        {reservations.length === 0 ? (
          <p>{t('inventoryDetail.noReservations')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('inventoryDetail.colQty')}</th>
                <th>{t('inventoryDetail.colCreated')}</th>
                <th>{t('inventoryDetail.colExpires')}</th>
              </tr>
            </thead>
            <tbody>
              {reservations.map((row, i) => {
                const r = row as Record<string, unknown>;
                return (
                  <tr key={i}>
                    <td>{formatNumber(String(r.qty), locale)}</td>
                    <td>{formatDateTime(String(r.created_at), locale)}</td>
                    <td>{r.expires_at ? formatDateTime(String(r.expires_at), locale) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('inventoryDetail.movementHistory')}</h2>
        {movements.length === 0 ? (
          <p>{t('inventoryDetail.noMovements')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('inventoryDetail.colReason')}</th>
                <th>{t('inventoryDetail.colQty')}</th>
                <th>{t('inventoryDetail.colDocument')}</th>
                <th>{t('inventoryDetail.colOccurred')}</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((row, i) => {
                const r = row as Record<string, unknown>;
                return (
                  <tr key={i}>
                    <td>
                      <StatusBadge value={String(r.reason)} />
                    </td>
                    <td>{formatNumber(String(r.qty), locale)}</td>
                    <td>
                      {r.doc_kind ? `${String(r.doc_kind)} / ${String(r.doc_id)}` : '—'}
                    </td>
                    <td>{formatDateTime(String(r.occurred_at), locale)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
